import { OpenAiReviewToolEventProperties } from "@/analytics/ai-detection-events-properties";
import { EventType } from "@/analytics/analytics-events";
import {
  selectAiDetectedMarkupsCount,
  selectFirstAiDetectedMarkupNotReviewedId,
} from "@/store/ai-asset-review/ai-asset-review-data-selectors";
import { setCurrentAnnotationId } from "@/store/ai-asset-review/ai-asset-review-slice";
import { changeMode } from "@/store/mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { AssetDetectionTask } from "@/utils/background-tasks";
import { NoTranslate } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { selectProjectName } from "@faro-lotv/project-source";
import {
  BackgroundTaskState,
  isBackgroundTaskActive,
} from "@faro-lotv/service-wires";
import { useCallback, type JSX } from "react";
import { CardError } from "../common/card-error";
import { CardProgress } from "../common/card-progress";
import { CardAction, GenericCardLayout } from "../layouts/generic-card-layout";
import {
  getTaskStatusLabel,
  getTaskTypeDescription,
} from "../task-descriptions";

type AssetDetectionTaskCardProps = {
  /** The task to show a card for */
  task: AssetDetectionTask;
};

/** @returns card for AI asset detection tasks */
export function AssetDetectionTaskCard({
  task,
}: AssetDetectionTaskCardProps): JSX.Element {
  const dispatch = useAppDispatch();
  const projectName = useAppSelector(selectProjectName);
  const firstNotReviewedId = useAppSelector(
    selectFirstAiDetectedMarkupNotReviewedId,
  );
  const totalDetectionsCount = useAppSelector(selectAiDetectedMarkupsCount);

  const handleReviewResults = useCallback(() => {
    // Track analytics event for opening AI review tool
    Analytics.track<OpenAiReviewToolEventProperties>(
      EventType.openAiDetectionReviewTool,
      {
        totalDetectionsToReview: totalDetectionsCount,
        via: "progress-overview",
      },
    );

    // Set the first annotation to review if it exists
    if (firstNotReviewedId) {
      dispatch(setCurrentAnnotationId(firstNotReviewedId));
    }

    // Switch to AI asset review mode
    dispatch(changeMode("aiAssetReview"));
  }, [dispatch, firstNotReviewedId, totalDetectionsCount]);

  // Create action button for reviewing results when task is completed
  const action: CardAction | undefined =
    task.state === BackgroundTaskState.succeeded
      ? {
          name: "Review results",
          callback: handleReviewResults,
        }
      : undefined;

  return (
    <GenericCardLayout
      name={
        projectName ? (
          <NoTranslate>{projectName}</NoTranslate>
        ) : (
          "Asset Detection"
        )
      }
      subText={getTaskTypeDescription(task)}
      startTime={task.createdAt}
      action={action}
    >
      {isBackgroundTaskActive(task.state) && (
        <CardProgress
          label={getTaskStatusLabel(task)}
          subLabel={
            task.progress > 0 && (
              <NoTranslate>{Math.round(task.progress)}%</NoTranslate>
            )
          }
          task={task}
        />
      )}
      {task.state === BackgroundTaskState.failed && <CardError task={task} />}
    </GenericCardLayout>
  );
}
